/** Shared product column helpers for MIS warranty / rejection / supply tables. */

export const BASE_PRODUCT_FIELDS = [
  'ABPB', 'BJRL', 'BJTL', 'PBS', 'RPPS', 'RPLBS', 'LBS', 'TCEJ', 'RS', 'MEP',
];

export const BASE_PRODUCT_FIELDS_SET = new Set(BASE_PRODUCT_FIELDS);

const ROW_META_FIELDS = new Set([
  'id', 'YearMonth', 'yearmonth', 'created_at', 'updated_at', 'isNew',
]);

/** Normalize product names so "POLY RING", "POLY_RING", and "poly ring" match. */
export function normalizeItemKey(s) {
  return String(s || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

/** Match backend sanitizeColumnName: POLY RING -> POLY_RING, TOP HOUSING -> TOP_HOUSING */
export function sanitizeToColumn(name) {
  return String(name || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^0-9A-Z_]/g, '_');
}

export function isBaseProduct(name) {
  return BASE_PRODUCT_FIELDS_SET.has(normalizeItemKey(name));
}

export function isAllowedProduct(name, mainProductsSet) {
  if (!mainProductsSet || mainProductsSet.size === 0) return false;
  const key = normalizeItemKey(name);
  for (const product of mainProductsSet) {
    if (normalizeItemKey(product) === key) return true;
  }
  return false;
}

export function resolveItemValue(agg, itemName) {
  if (!agg) return undefined;
  if (itemName in agg) return agg[itemName];
  const target = normalizeItemKey(itemName);
  for (const k of Object.keys(agg)) {
    if (normalizeItemKey(k) === target) return agg[k];
  }
  return undefined;
}

/** Read a product value from a warranty/rejection/supply row (handles display + column keys). */
export function resolveRowProductValue(row, itemName) {
  if (!row) return undefined;
  const column = sanitizeToColumn(itemName);
  if (row[column] != null && row[column] !== '') return row[column];
  if (row[itemName] != null && row[itemName] !== '') return row[itemName];
  const target = normalizeItemKey(itemName);
  for (const k of Object.keys(row)) {
    if (ROW_META_FIELDS.has(k) || isBaseProduct(k)) continue;
    if (normalizeItemKey(k) === target) return row[k];
  }
  return undefined;
}

/**
 * Build dynamic product display names strictly from main_product.
 * Complaint pivot items / API row keys may refine display labels, but items
 * present only in Excel/complaints (e.g. TEST, KLMN) are never shown as columns.
 */
export function collectDynamicProductNames(mainProductsSet, complaintItems = [], rows = []) {
  const names = new Map();

  const addName = (raw) => {
    const display = String(raw || '').trim().replace(/\s+/g, ' ').toUpperCase();
    if (!display || isBaseProduct(display)) return;
    // Gate every candidate on main_product — Excel-only items must not become columns
    if (!isAllowedProduct(display, mainProductsSet)) return;
    const key = normalizeItemKey(display);
    if (!names.has(key)) names.set(key, display);
  };

  // Prefer display names from complaints / rows when they match main_product
  if (Array.isArray(complaintItems)) {
    complaintItems.forEach(addName);
  }
  if (Array.isArray(rows)) {
    rows.forEach((row) => {
      if (!row || typeof row !== 'object') return;
      Object.keys(row).forEach((k) => {
        if (ROW_META_FIELDS.has(k) || isBaseProduct(k)) return;
        addName(k.replace(/_/g, ' '));
      });
    });
  }
  // Always include every main_product item as a column
  if (mainProductsSet) {
    Array.from(mainProductsSet).forEach(addName);
  }

  return Array.from(names.values()).sort((a, b) => a.localeCompare(b));
}
